var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { Button } from '@paysera/ui';
import { yupResolver } from '@hookform/resolvers/yup';
import { useTranslation } from 'react-i18next';
import { useLazyGetFunctionalitiesQuery } from '@app/api/functionalitiesApi';
import { useGetActiveLoansQuery } from '@app/api/loanApplicationsApi';
import { useFetchCurrentSolvencyDeclarationQuery, useFetchSolvencyDeclarationQuestionnaireFormQuery, useSubmitSolvencyDeclarationMutation, } from '@app/api/solvencyDeclarationApi';
import ClosingTime from '@app/components/alerts/ClosingTime';
import { PageLoader } from '@app/components/PageLoader';
import ApplicationIncomplete from '@app/components/popups/ApplicationIncomplete';
import QuestionnaireSent from '@app/components/popups/QuestionnaireSent';
import Stepper from '@app/components/stepper/Stepper';
import { ACTIVE_LOAN_APPLICATION_STATUSES } from '@app/configs/loanApplication';
import { NATIVE_NOTIFICATIONS } from '@app/configs/nativeNotifications';
import { ROUTES } from '@app/configs/routes';
import { FIELDS_STEPS_MAPING } from '@app/configs/solvencyDeclaration';
import { nativeNotifications } from '@app/utils/nativeNotifications';
import { isLocalExpirationWithin30Minutes } from '@app/utils/time';
import SolvencyDeclarationForm from '@modules/borrower/components/solvencyDeclaration/SolvencyDeclarationForm';
import { useNavigationBlocker } from '@modules/borrower/hooks/useNavigationBlocker';
import { generateYupSchema } from '@modules/borrower/utils/generateYupSchema';
import sdLogo from '@assets/sd_logo.png';
const SolvencyDeclaration = ({ isMobile }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [isApplicationIncomplete, setIsApplicationIncomplete] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [allowNavigation, setAllowNavigation] = useState(false);
    const { data: loanApplications = [], isLoading: loadingLoanApplication, isFetching: fetchingLoanApplication, } = useGetActiveLoansQuery();
    const hasActiveLoan = loanApplications.some((loan) => ACTIVE_LOAN_APPLICATION_STATUSES.includes(loan.status));
    useEffect(() => {
        if (hasActiveLoan) {
            navigate(ROUTES.home);
        }
    }, [hasActiveLoan]);
    const { data: questionnaireForm, isLoading: questionnaireFormLoading } = useFetchSolvencyDeclarationQuestionnaireFormQuery();
    const { data: solvencyDeclaration } = useFetchCurrentSolvencyDeclarationQuery();
    const [updateFunctionalities, { isLoading: isFetchingFunctionalities }] = useLazyGetFunctionalitiesQuery();
    const [submitSolvencyDeclaration, { isLoading: submitLoading }] = useSubmitSolvencyDeclarationMutation();
    const [openSuccessPopup, setOpenSuccessPopup] = useState(false);
    const [activeStep, setActiveStep] = useState(1);
    const [steps, setSteps] = useState([
        { step: 1, text: t('financing.solvency_declaration.steps.general'), completed: false },
        { step: 2, text: t('financing.solvency_declaration.steps.income'), completed: false },
        { step: 3, text: t('financing.solvency_declaration.steps.loan'), completed: false },
    ]);
    const isExpirationLessThan30Minutes = isLocalExpirationWithin30Minutes(solvencyDeclaration === null || solvencyDeclaration === void 0 ? void 0 : solvencyDeclaration.expiresAt);
    const schema = questionnaireForm ? generateYupSchema(questionnaireForm) : null;
    const form = useForm({
        resolver: schema ? yupResolver(schema) : undefined,
    });
    const fieldsToValidate = Object.keys(FIELDS_STEPS_MAPING).filter((field) => FIELDS_STEPS_MAPING[field] === activeStep);
    const setStep = (step) => {
        setActiveStep(step);
        setSteps(steps.map((item) => (Object.assign(Object.assign({}, item), { completed: item.step < step }))));
    };
    const validateCurrentStep = () => __awaiter(void 0, void 0, void 0, function* () {
        const isValid = yield form.trigger(fieldsToValidate);
        return isValid;
    });
    const validateField = (fieldName) => __awaiter(void 0, void 0, void 0, function* () {
        const isValid = yield form.trigger(fieldName);
        return isValid;
    });
    const handleGoToStep = (step) => {
        if (step > activeStep) {
            validateCurrentStep().then((isValid) => {
                if (isValid) {
                    setStep(step);
                }
            });
        }
        else {
            setStep(step);
        }
    };
    const handleGoToPreviousStep = () => {
        if (activeStep === 1) {
            return;
        }
        setActiveStep((curStep) => curStep - 1);
        setSteps(steps.map((stepItem, index) => {
            if (index === activeStep - 2) {
                return Object.assign(Object.assign({}, stepItem), { completed: false });
            }
            return stepItem;
        }));
    };
    const handleGoToNextStep = () => {
        if (activeStep === 3) {
            return;
        }
        validateCurrentStep().then((isValid) => {
            if (isValid) {
                handleGoToStep(activeStep + 1);
            }
        });
    };
    const handleGoToLoans = () => {
        updateFunctionalities();
        setAllowNavigation(true);
        navigate(ROUTES.home);
    };
    const handleOpenIncompleteModal = () => {
        setIsApplicationIncomplete(true);
    };
    const onSubmitSuccess = (data) => {
        if (!(data === null || data === void 0 ? void 0 : data.error)) {
            setIsSubmitting(false);
            setAllowNavigation(true);
            if (isMobile) {
                nativeNotifications.notify(NATIVE_NOTIFICATIONS.questionnaireFilled);
            }
            else {
                setOpenSuccessPopup(true);
            }
        }
        else {
            setIsSubmitting(false);
        }
    };
    const handleSubmit = () => __awaiter(void 0, void 0, void 0, function* () {
        setIsSubmitting(true);
        yield updateFunctionalities();
        validateCurrentStep().then((isValid) => {
            if (isValid) {
                const formData = form.getValues();
                const dataMapped = Object.keys(FIELDS_STEPS_MAPING).reduce((acc, key) => {
                    if (key.includes('group')) {
                        return acc;
                    }
                    return Object.assign(Object.assign({}, acc), { [key]: formData[key] ? `${formData[key]}` : '' });
                }, {});
                submitSolvencyDeclaration(dataMapped)
                    .then(onSubmitSuccess)
                    .catch(() => {
                    setIsSubmitting(false);
                });
            }
            else {
                setIsSubmitting(false);
            }
        });
    });
    const navigationBlocker = useNavigationBlocker({
        isBlocked: !allowNavigation && !isSubmitting && !isMobile,
        onBlock: handleOpenIncompleteModal,
    });
    const handleCloseIncompleteModalWithBlocker = () => {
        setIsApplicationIncomplete(false);
        navigationBlocker.reset();
        setAllowNavigation(false);
    };
    const handleGoToLoansWithBlocker = () => {
        setAllowNavigation(true);
        updateFunctionalities();
        if (navigationBlocker.isBlocked) {
            navigationBlocker.proceed();
        }
        else {
            navigate(ROUTES.home);
        }
    };
    if (questionnaireFormLoading || loadingLoanApplication || fetchingLoanApplication || hasActiveLoan) {
        return <PageLoader />;
    }
    const footerButtons = (<>
            {!isMobile && activeStep > 1 && (<Button type="button" variant="ghost" onClick={handleGoToPreviousStep} label={t('financing.solvency_declaration.previous_step')}/>)}
            {activeStep < 3 && (<Button type="button" variant="primary" onClick={handleGoToNextStep} label={isMobile
                ? t('financing.solvency_declaration.continue')
                : t('financing.solvency_declaration.next_step')}/>)}
            {activeStep === 3 && (<Button type="button" variant="primary" onClick={handleSubmit} loading={isFetchingFunctionalities || submitLoading} disabled={isFetchingFunctionalities || submitLoading} label={t('financing.solvency_declaration.submit')}/>)}
        </>);
    if (isMobile) {
        return (<div className="p-2">
                {isExpirationLessThan30Minutes && (<div className="mb-2">
                        <ClosingTime expiresAt={solvencyDeclaration === null || solvencyDeclaration === void 0 ? void 0 : solvencyDeclaration.expiresAt}/>
                    </div>)}
                <Stepper activeStep={activeStep} steps={steps} goToStep={handleGoToStep} withNavigation/>
                <div className="mt-4">
                    <SolvencyDeclarationForm form={form} step={activeStep} questionnaireForm={questionnaireForm} onSubmit={handleSubmit} validateField={validateField}/>
                </div>
                <div className="mt-4 pt-4 border-t border-neutral-primary-initial flex flex-col gap-2">
                    {footerButtons}
                </div>
                <QuestionnaireSent isOpen={openSuccessPopup} closeModal={handleGoToLoans}/>
            </div>);
    }
    return (<>
            <ApplicationIncomplete title={t('financing.solvency_declaration.incomplete_title')} warning={t('financing.application_incomplete.leave_warning')} isOpen={isApplicationIncomplete} closeModal={handleCloseIncompleteModalWithBlocker} onProceed={handleGoToLoansWithBlocker}/>

            <div className="mt-4 md:mt-6">
                <div className="flex items-center justify-between mb-6">
                    <div>
                        <h1 className="headline-l text-action-primary-initial">
                            {t('financing.loan_application.title')}
                        </h1>
                        <p className="headline-l">{t('financing.solvency_declaration.questionnaire_title')}</p>
                    </div>
                    <div>
                        <img src={sdLogo} alt="SD Logo"/>
                    </div>
                </div>

                {isExpirationLessThan30Minutes && (<div className="mb-4">
                        <ClosingTime expiresAt={solvencyDeclaration === null || solvencyDeclaration === void 0 ? void 0 : solvencyDeclaration.expiresAt}/>
                    </div>)}

                <Stepper activeStep={activeStep} steps={steps} goToStep={handleGoToStep} withNavigation/>

                <div className="mt-6">
                    <SolvencyDeclarationForm form={form} step={activeStep} questionnaireForm={questionnaireForm} onSubmit={handleSubmit} validateField={validateField}/>
                </div>

                <div className="mt-6 pt-4 border-t border-neutral-primary-initial flex justify-end gap-4">
                    {footerButtons}
                </div>
            </div>
            <QuestionnaireSent isOpen={openSuccessPopup} closeModal={handleGoToLoans}/>
        </>);
};
export default SolvencyDeclaration;
